import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ButtonDestinationEditor } from "../inspector/LandingInspectorControls";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { persistedCssColorError, registryFieldError } from "../inspector/inspectorModel";
import { VisualColorControl } from "../inspector/VisualSettingControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { buildInternalDestinationOptions, getValueAtDescriptorPath } from "../elementEditing";

function ctaColorFallback(field, form) {
  if (field?.explicitVisual && String(field?.defaultValue || "").trim()) return String(field.defaultValue).trim();
  const path = String(field?.path || "");
  const secondary = path.includes("hero.secondaryCtaStyle.");
  const key = path.split(".").pop();
  const primaryColor = form?.theme?.primaryColor || "#E44E31";
  const secondaryColor = form?.theme?.secondaryColor || "#494A98";

  if (key === "textColor" || key === "hoverTextColor") return "#ffffff";
  if (key === "backgroundColor") return secondary ? "#ffffff" : primaryColor;
  if (key === "hoverBackgroundColor") return secondary ? "#ffffff" : secondaryColor;
  if (key === "borderColor") return "rgba(255,255,255,.7)";
  if (key === "shadowColor") return "#000000";
  return field?.fallbackValue || field?.overrideSeedValue || "#000000";
}

function ctaColorContractError(field, value) {
  return registryFieldError(field, value) || persistedCssColorError(value, field?.maxLength || 100);
}

function CtaColorFieldControl({ field, form, disabled, onCommit }) {
  const rawValue = getValueAtDescriptorPath(form, field.path, "");
  const ownValue = String(rawValue ?? "").trim();
  const fallback = ctaColorFallback(field, form);
  return (
    <VisualColorControl
      label={field.label}
      value={ownValue}
      fallback={fallback}
      disabled={disabled}
      helperText={field.explicitVisual
        ? (field.path?.startsWith("navigation.megaMenuVisual.") ? "Valor visual explícito del Mega Menú." : "Color explícito de este botón.")
        : (ownValue ? "Color propio del CTA." : "Sin override, usa el estilo heredado del botón.")}
      allowExactCss
      validateExactValue={(candidate) => ctaColorContractError(field, candidate)}
      exactValueMaxLength={field.maxLength || 100}
      exactValueHelperText={field.explicitVisual
        ? "Admite HEX, rgb/rgba, hsl/hsla y transparent. Restablecer vuelve al valor explícito por defecto."
        : "Admite HEX, rgb/rgba, hsl/hsla y transparent. Vacío vuelve a heredar."}
      onChange={(next) => onCommit?.(field.path, next)}
      onReset={() => onCommit?.(field.path, field.explicitVisual ? (field.defaultValue || fallback) : "")}
    />
  );
}

function FieldControl({ field, form, baselineForm, disabled, onCommit }) {
  if (!field) return null;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return <FormControlLabel control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />} label={field.label} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return (
      <FormControl fullWidth size="small" disabled={disabled}>
        <InputLabel>{field.label}</InputLabel>
        <Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>
          {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
        </Select>
      </FormControl>
    );
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <TextField fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return <CtaColorFieldControl field={field} form={form} disabled={disabled} onCommit={onCommit} />;
  }
  return <LandingCopyTextField fullWidth size="small" label={field.label} field={field} path={field.path} form={form} baselineForm={baselineForm} value={value ?? ""} disabled={disabled} multiline={field.type === SEGMENT_FIELD_TYPES.TEXTAREA} minRows={field.type === SEGMENT_FIELD_TYPES.TEXTAREA ? 3 : undefined} commitMode="change" onCommit={(nextValue) => onCommit?.(field.path, nextValue)} />;
}

export default function ButtonElementEditor({ descriptor, form, baselineForm, disabled, onCommit }) {
  const labelField = descriptor?.labelField;
  if (!labelField) return null;
  const labelValue = getValueAtDescriptorPath(form, labelField.path, "");
  const options = buildInternalDestinationOptions(form);
  const showField = descriptor?.showField;
  const requiresField = descriptor?.requiresProductsField || (descriptor?.extraFields || []).find((field) => /RequiresProducts$/.test(field?.path || ""));
  const remainingExtra = (descriptor?.extraFields || []).filter((field) => field !== requiresField);
  const styleFields = descriptor?.styleFields || [];
  const showIconField = descriptor?.showIconField;
  const iconField = descriptor?.iconField;
  const urlField = descriptor?.urlField;
  const behaviorField = descriptor?.behaviorField;
  const targetField = descriptor?.targetField;
  const sharedField = [labelField, urlField, showField, requiresField, behaviorField, targetField, ...remainingExtra]
    .find((field) => field?.shared);
  const megaMenuFields = descriptor?.megaMenuFields || [];
  const megaMenuContentFields = megaMenuFields.filter((field) => !field?.megaMenuGroup);
  const megaMenuVisualGroups = megaMenuFields.reduce((groups, field) => {
    if (!field?.megaMenuGroup) return groups;
    if (!groups[field.megaMenuGroup]) groups[field.megaMenuGroup] = [];
    groups[field.megaMenuGroup].push(field);
    return groups;
  }, {});
  const megaVisual = form?.navigation?.megaMenuVisual || {};

  return (
    <Stack spacing={1.35}>
      <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
        {(descriptor?.chips || []).map((chip) => <Chip key={chip} size="small" label={chip} variant="outlined" />)}
        {sharedField ? <Chip size="small" label="Se usa también en otras secciones" variant="outlined" /> : null}
        {requiresField && getValueAtDescriptorPath(form, requiresField.path, false) ? <Chip size="small" label="Catálogo" /> : null}
        {showIconField && getValueAtDescriptorPath(form, showIconField.path, false) ? <Chip size="small" label="Con icono" /> : null}
      </Stack>

      <Box sx={{ p: 1.2, borderRadius: 2.5, border: "1px solid rgba(25,36,47,.1)", bgcolor: "rgba(25,36,47,.025)" }}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Vista previa</Typography>
        <Box sx={{ mt: 0.8, display: "inline-flex", px: 2, py: 1, borderRadius: 999, bgcolor: "primary.main", color: "primary.contrastText", fontWeight: 900 }}>
          {labelValue || "Botón"}
        </Box>
      </Box>

      <LandingCopyTextField fullWidth size="small" label={labelField.label || "Texto del botón"} field={labelField} path={labelField.path} form={form} baselineForm={baselineForm} value={labelValue} disabled={disabled} commitMode="change" onCommit={(nextValue) => onCommit?.(labelField.path, nextValue)} />

      {showField ? <FieldControl field={showField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {behaviorField ? <FieldControl field={behaviorField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {targetField ? <FieldControl field={targetField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}

      {urlField ? (
        urlField.destinationPicker ? (
          <ButtonDestinationEditor field={urlField} value={getValueAtDescriptorPath(form, urlField.path, "")} options={options} disabled={disabled} onCommit={onCommit} />
        ) : <FieldControl field={urlField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
      ) : (!targetField ? <Chip size="small" label="Destino definido por esta sección" variant="outlined" sx={{ alignSelf: "flex-start" }} /> : null)}

      {requiresField ? <FieldControl field={requiresField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}
      {remainingExtra.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}

      {showIconField || iconField ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Icono</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{showIconField ? <FieldControl field={showIconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}{iconField && (!showIconField || getValueAtDescriptorPath(form, showIconField.path, true) !== false) ? <FieldControl field={iconField} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} /> : null}</Stack></AccordionDetails>
        </Accordion>
      ) : null}

      {(descriptor?.megaMenu || (descriptor?.megaMenuFields || []).length || (descriptor?.megaMenuOverrideFields || []).length) ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Configurar mega menú</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {(descriptor?.megaMenuOverrideFields || []).map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
              {megaMenuContentFields.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}
              {Object.keys(megaMenuVisualGroups).length ? (
                <Box sx={{ mt: 0.5, p: 1.2, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5, bgcolor: megaVisual?.surface?.backgroundColor || "#F7F8FC" }}>
                  <Typography variant="overline" sx={{ color: megaVisual?.eyebrow?.color || "#E44E31", fontWeight: 900 }}>COLECCIÓN</Typography>
                  <Typography sx={{ color: megaVisual?.title?.color || "#494A98", fontWeight: 950, lineHeight: 1.15 }}>{form?.navigation?.megaMenuTitle || "Explorá el catálogo"}</Typography>
                  <Typography variant="caption" sx={{ color: megaVisual?.description?.color || "#5B5D73" }}>{form?.navigation?.megaMenuDescription || "Vista previa visual del Mega Menú"}</Typography>
                  <Stack direction="row" spacing={0.6} sx={{ mt: 1 }}>
                    <Box sx={{ px: 1, py: 0.45, borderRadius: 999, color: megaVisual?.categoryPill?.textColor || "#5B5D73", bgcolor: megaVisual?.categoryPill?.backgroundColor || "#EEF0F5", border: "1px solid", borderColor: megaVisual?.categoryPill?.borderColor || "transparent", fontSize: ".72rem", fontWeight: 800 }}>Categoría</Box>
                    <Box sx={{ px: 1, py: 0.45, borderRadius: 999, color: megaVisual?.allItemsButton?.textColor || "#494A98", bgcolor: megaVisual?.allItemsButton?.backgroundColor || "transparent", border: "1px solid", borderColor: megaVisual?.allItemsButton?.borderColor || "transparent", fontSize: ".72rem", fontWeight: 800 }}>Ver todos</Box>
                  </Stack>
                </Box>
              ) : null}
              {Object.entries(megaMenuVisualGroups).map(([group, fields]) => (
                <Accordion key={group} disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.08)", borderRadius: "10px !important", "&::before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 850 }}>{group}</Typography></AccordionSummary>
                  <AccordionDetails><Stack spacing={1}>{fields.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {styleFields.length ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia y hover</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((field) => <FieldControl key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
